import React, { useState } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { aiApi } from '../../../api/ai';
import { Sparkles } from 'lucide-react';

interface JobDetailsData {
  job_title: string;
  department: string;
  application_deadline: string;
  required_number_of_references: number;
  description: string;
}

interface JobDetailsProps {
  data: JobDetailsData;
  onChange: (data: JobDetailsData) => void;
}

const JobDetails = ({ data, onChange }: JobDetailsProps) => {
  const { t } = useLanguage();
  const [customDepartment, setCustomDepartment] = useState('');
  const [showCustomDepartment, setShowCustomDepartment] = useState(false);
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);

  const defaultDepartments = [
    'Engineering',
    'Product',
    'Design',
    'Marketing',
    'Sales',
    'HR',
    'Finance',
    'Operations',
    'Comercial'
  ];

  const handleDepartmentChange = (value: string) => {
    if (value === 'custom') {
      setShowCustomDepartment(true);
      onChange({ ...data, department: customDepartment });
    } else {
      setShowCustomDepartment(false);
      onChange({ ...data, department: value });
    }
  };

  const handleCustomDepartmentChange = (value: string) => {
    setCustomDepartment(value);
    onChange({ ...data, department: value });
  };

  const handleGenerateDescription = async () => {
    if (!data.job_title || !data.department) {
      setGenerationError(t('dashboard.newJobModal.jobDetails.aiNeedsTitleAndDepartment'));
      return;
    }

    setIsGeneratingDescription(true);
    setGenerationError(null);

    try {
      // User requirements could be empty, but title and department are required
      const result = await aiApi.generateJobDescription({
        job_title: data.job_title,
        department: data.department,
        user_requirements: '' // Since we don't have requirements at this step
      });

      // Handle response that might have either description or job_description field
      const jobDescription = result.description || result.job_description || '';
      onChange({ ...data, description: jobDescription });
    } catch (error) {
      console.error('Failed to generate job description:', error);
      setGenerationError(t('dashboard.newJobModal.jobDetails.aiGenerationError'));
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('dashboard.newJobModal.jobDetails.jobTitle')}
        </label>
        <input
          type="text"
          value={data.job_title}
          onChange={(e) => onChange({ ...data, job_title: e.target.value })}
          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
          placeholder={t('dashboard.newJobModal.jobDetails.jobTitlePlaceholder')}
        />
      </div>

      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700">
          {t('dashboard.newJobModal.jobDetails.department')}
        </label>
        <select
          value={showCustomDepartment ? 'custom' : data.department}
          onChange={(e) => handleDepartmentChange(e.target.value)}
          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="">{t('dashboard.newJobModal.jobDetails.selectDepartment')}</option>
          {defaultDepartments.map((dept) => (
            <option key={dept} value={dept}>{t(`departments.${dept}`)}</option>
          ))}
          <option value="custom">{t('dashboard.newJobModal.jobDetails.customDepartment')}</option>
        </select>

        {showCustomDepartment && (
          <input
            type="text"
            value={customDepartment}
            onChange={(e) => handleCustomDepartmentChange(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder={t('dashboard.newJobModal.jobDetails.customDepartmentPlaceholder')}
          />
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('dashboard.newJobModal.jobDetails.referencesRequired')}
        </label>
        <select
          value={data.required_number_of_references}
          onChange={(e) => onChange({ ...data, required_number_of_references: Number(e.target.value) })}
          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          {[1, 2, 3, 4, 5].map((num) => (
            <option key={num} value={num}>
              {num} {num === 1 ? t('dashboard.newJobModal.jobDetails.reference') : t('dashboard.newJobModal.jobDetails.references')}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('dashboard.newJobModal.jobDetails.deadline')}
        </label>
        <input
          type="date"
          value={data.application_deadline}
          onChange={(e) => onChange({ ...data, application_deadline: e.target.value })}
          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
          min={new Date().toISOString().split('T')[0]}
        />
      </div>
      
      <div>
        <div className="flex justify-between items-center mb-1">
          <label className="block text-sm font-medium text-gray-700">
            {t('dashboard.newJobModal.jobDetails.description')}
          </label>
          <button
            type="button"
            onClick={handleGenerateDescription}
            disabled={isGeneratingDescription}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 text-sm font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isGeneratingDescription ? (
              <span className="h-4 w-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"/>
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
            {isGeneratingDescription 
              ? t('dashboard.newJobModal.jobDetails.aiGenerating') 
              : t('dashboard.newJobModal.jobDetails.aiGenerate')}
          </button>
        </div>
        <textarea
          value={data.description || ''}
          onChange={(e) => onChange({ ...data, description: e.target.value })}
          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
          placeholder={t('dashboard.newJobModal.jobDetails.descriptionPlaceholder')}
          rows={4}
        />
        {generationError && (
          <p className="mt-1 text-xs text-red-600">{generationError}</p>
        )}
      </div>
    </div>
  );
};

export default JobDetails;